import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { Opcion, Pagina, Tecnico } from '../api/tipos';
import { useSesion } from '../sesion';
import {
  Cargando, Dialogo, DialogoMotivo, Error, Insignia, Paginacion,
} from '../componentes/comunes';

/**
 * Técnicos: a quién se le asigna una orden y a quién se le paga.
 *
 * No son usuarios del sistema, no tienen contraseña. Los 32 que se
 * migraron vinieron sin datos bancarios porque APRISA todavía no los
 * recaba; eso no impide asignarles trabajo, sólo pagarles.
 */
export default function Tecnicos() {
  const { puede } = useSesion();

  const [busqueda, setBusqueda] = useState('');
  const [regionId, setRegionId] = useState('');
  const [estado, setEstado] = useState('');
  const [bancarios, setBancarios] = useState('');
  const [numero, setNumero] = useState(1);
  const [pagina, setPagina] = useState<Pagina<Tecnico> | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [regiones, setRegiones] = useState<Opcion[]>([]);
  const [areas, setAreas] = useState<Opcion[]>([]);
  const [bancos, setBancos] = useState<Opcion[]>([]);

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Tecnico | null>(null);
  const [viendo, setViendo] = useState<Tecnico | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState<Tecnico | null>(null);

  useEffect(() => {
    api.get<Opcion[]>('/catalogos/regiones/opciones').then(setRegiones).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/areas/opciones').then(setAreas).catch(() => undefined);
    api.get<Opcion[]>('/catalogos/bancos/opciones').then(setBancos).catch(() => undefined);
  }, []);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<Tecnico>>('/tecnicos', {
      busqueda: busqueda || undefined,
      regionId: regionId || undefined,
      estado: estado || undefined,
      conDatosBancarios: bancarios || undefined,
      pagina: numero,
      limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [busqueda, regionId, estado, bancarios, numero]);

  useEffect(() => {
    const t = setTimeout(cargar, busqueda ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargar, busqueda]);

  const sinBancarios = pagina?.data.filter((t) => !t.tieneDatosBancarios).length ?? 0;

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Técnicos</h1>
          <p className="sub">
            A quién se asigna una orden y a quién se le paga. La región
            determina qué técnicos se ofrecen para cada sucursal.
          </p>
        </div>
        {puede('usuarios', 'crear') && (
          <button className="primario" onClick={() => setCreando(true)}>Nuevo técnico</button>
        )}
      </div>

      <div className="panel">
        <div className="controles">
          <input
            type="search"
            placeholder="Nombre, correo o teléfono…"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setNumero(1); }}
          />
          <select value={regionId} onChange={(e) => { setRegionId(e.target.value); setNumero(1); }}>
            <option value="">Todas las regiones</option>
            {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
          <select value={estado} onChange={(e) => { setEstado(e.target.value); setNumero(1); }}>
            <option value="">Todos</option>
            <option value="activo">Sólo activos</option>
            <option value="inactivo">Sólo inactivos</option>
          </select>
          <select
            value={bancarios}
            onChange={(e) => { setBancarios(e.target.value); setNumero(1); }}
          >
            <option value="">Con y sin datos bancarios</option>
            <option value="si">Con datos bancarios</option>
            <option value="no">Sin datos bancarios</option>
          </select>
        </div>

        <Error error={error} />

        {sinBancarios > 0 && !bancarios && (
          <div className="aviso" style={{ marginBottom: 12 }}>
            {sinBancarios} de los técnicos de esta página no tienen cuenta
            capturada. Se les puede asignar trabajo, pero no pagarles.
          </div>
        )}

        {cargando && !pagina ? <Cargando /> : pagina && (
          <>
            {pagina.data.length === 0 ? (
              <div className="vacio">No hay técnicos que cumplan esos filtros.</div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Región</th>
                    <th>Especialidad</th>
                    <th>Teléfono</th>
                    <th>Banco</th>
                    <th>CLABE</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pagina.data.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <button className="enlace" onClick={() => setViendo(t)}>
                          {t.nombreCompleto}
                        </button>
                      </td>
                      <td>{t.region?.nombre ?? '—'}</td>
                      <td>{t.especialidad?.nombre ?? '—'}</td>
                      <td>{t.telefono ?? '—'}</td>
                      <td>{t.banco?.nombre ?? '—'}</td>
                      <td className="mono">{t.clabe ?? '—'}</td>
                      <td><Insignia activo={t.activo} /></td>
                      <td className="acciones">
                        {puede('usuarios', 'editar') && (
                          <>
                            <button className="enlace" onClick={() => setEditando(t)}>Editar</button>
                            <button className="enlace" onClick={() => setCambiandoEstado(t)}>
                              {t.activo ? 'Dar de baja' : 'Reactivar'}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <Paginacion
              pagina={pagina.pagina}
              limite={pagina.limite}
              total={pagina.total}
              onCambiar={setNumero}
            />
          </>
        )}
      </div>

      {viendo && <Ficha id={viendo.id} onCerrar={() => setViendo(null)} />}

      {(creando || editando) && (
        <FormularioTecnico
          regiones={regiones}
          areas={areas}
          bancos={bancos}
          tecnico={editando}
          onCerrar={() => { setCreando(false); setEditando(null); }}
          onGuardado={() => { setCreando(false); setEditando(null); cargar(); }}
        />
      )}

      {cambiandoEstado && (
        <CambioEstadoTecnico
          tecnico={cambiandoEstado}
          onCerrar={() => setCambiandoEstado(null)}
          onHecho={() => { setCambiandoEstado(null); cargar(); }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════ ficha

function Ficha({ id, onCerrar }: { id: string; onCerrar: () => void }) {
  const [tecnico, setTecnico] = useState<Tecnico | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api.get<Tecnico>(`/tecnicos/${id}`).then(setTecnico).catch(setError);
  }, [id]);

  return (
    <Dialogo
      titulo={tecnico?.nombreCompleto ?? 'Técnico'}
      onCerrar={onCerrar}
      pie={<button onClick={onCerrar}>Cerrar</button>}
    >
      <Error error={error} />
      {!tecnico ? <Cargando /> : (
        <>
          <Dato etiqueta="Región de despacho" valor={tecnico.region?.nombre ?? null} />
          <Dato etiqueta="Especialidad" valor={tecnico.especialidad?.nombre ?? null} />
          <Dato etiqueta="Teléfono" valor={tecnico.telefono} />
          <Dato etiqueta="Correo" valor={tecnico.correo} />
          <Dato etiqueta="RFC" valor={tecnico.rfc} mono />
          <Dato
            etiqueta="Acceso al sistema"
            valor={tecnico.tieneAcceso ? 'Sí' : 'No, sólo recibe asignaciones'}
          />

          <h2>Datos bancarios</h2>

          {/* La misma persona en dos catálogos es donde se paga a la
              CLABE equivocada: se corrige en un registro y el otro se
              queda con la vieja. Lo acordado es que mande el de
              proveedor, y decirlo aquí cuesta menos que recordarlo. */}
          {tecnico.tambienProveedor && (
            <div className="aviso">
              Esta persona también está como proveedor
              (<strong>{tecnico.tambienProveedor.codigo}</strong>
              {' · '}{tecnico.tambienProveedor.nombre}), que es como se le
              pagan los TOT. <strong>La cuenta buena es la de allá</strong>:
              conviene dejar ésta vacía, para no acabar con dos versiones de
              la misma CLABE compitiendo.
            </div>
          )}

          {!tecnico.tieneDatosBancarios ? (
            <div className="aviso">
              {tecnico.tambienProveedor
                // Sin cuenta aquí pero con registro de proveedor no es un
                // hueco: es lo acordado. Regañar por algo que se hizo bien
                // enseña a ignorar los avisos.
                ? <>Sin cuenta aquí, que es lo correcto en este caso: se le
                   paga por su registro de proveedor. Un pago al técnico
                   —anticipo o viáticos— sí necesitaría una cuenta en esta
                   ficha.</>
                : <>Sin cuenta capturada. Se le puede asignar trabajo, pero no
                   pagarle.</>}
            </div>
          ) : (
            <>
              {!tecnico.datosBancariosVisibles && (
                <div className="aviso">
                  Se muestran enmascarados: tu perfil no tiene el permiso de
                  ver datos bancarios.
                </div>
              )}
              <Dato etiqueta="Banco" valor={tecnico.banco?.nombre ?? null} />
              <Dato etiqueta="Titular" valor={tecnico.titularCuenta} />
              <Dato
                etiqueta="Pago de sus vales"
                valor={tecnico.valesEnGrupo
                  ? 'En grupo, por requerimiento'
                  : 'Uno por uno, al autorizar'}
              />
              <Dato etiqueta="Cuenta" valor={tecnico.numeroCuenta} mono />
              <Dato etiqueta="CLABE" valor={tecnico.clabe} mono />
              {tecnico.datosBancariosVisibles && (
                <div className="aviso">
                  Esta consulta quedó registrada en la bitácora de datos sensibles.
                </div>
              )}
            </>
          )}
        </>
      )}
    </Dialogo>
  );
}

function Dato({ etiqueta, valor, mono }: { etiqueta: string; valor: string | null; mono?: boolean }) {
  return (
    <div className="campo">
      <label>{etiqueta}</label>
      <span className={mono ? 'mono' : undefined}>{valor ?? '—'}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════ formulario

function FormularioTecnico({
  regiones, areas, bancos, tecnico, onCerrar, onGuardado,
}: {
  regiones: Opcion[];
  areas: Opcion[];
  bancos: Opcion[];
  tecnico: Tecnico | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esAlta = tecnico === null;
  const [datos, setDatos] = useState({
    nombre: tecnico?.nombre ?? '',
    apellidos: tecnico?.apellidos ?? '',
    telefono: tecnico?.telefono ?? '',
    correo: tecnico?.correo ?? '',
    rfc: tecnico?.rfc ?? '',
    regionId: tecnico?.region?.id ?? '',
    especialidadId: tecnico?.especialidad?.id ?? '',
    bancoId: tecnico?.banco?.id ?? '',
    titularCuenta: tecnico?.titularCuenta ?? '',
    valesEnGrupo: tecnico?.valesEnGrupo ?? false,
    numeroCuenta: '',
    clabe: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function guardar() {
    setError(null);
    setTrabajando(true);
    try {
      const cuerpo: Record<string, unknown> = {
        nombre: datos.nombre.trim(),
        apellidos: datos.apellidos.trim() || undefined,
        telefono: datos.telefono.trim() || undefined,
        correo: datos.correo.trim() || undefined,
        rfc: datos.rfc.trim() || undefined,
        regionId: datos.regionId || undefined,
        especialidadId: datos.especialidadId || undefined,
        bancoId: datos.bancoId || undefined,
        titularCuenta: datos.titularCuenta.trim() || undefined,
        valesEnGrupo: datos.valesEnGrupo,
      };

      // Los campos bancarios sólo viajan si se escribió algo: se abren
      // vacíos porque no se descargan en claro, y mandarlos vacíos
      // borraría la cuenta que ya estuviera guardada.
      if (datos.numeroCuenta.trim()) cuerpo.numeroCuenta = datos.numeroCuenta.trim();
      if (datos.clabe.trim()) cuerpo.clabe = datos.clabe.trim();

      if (esAlta) await api.post('/tecnicos', cuerpo);
      else await api.patch(`/tecnicos/${tecnico!.id}`, cuerpo);

      onGuardado();
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Dialogo
      titulo={esAlta ? 'Nuevo técnico' : `Editar ${tecnico!.nombreCompleto}`}
      onCerrar={onCerrar}
      pie={
        <>
          <button onClick={onCerrar} disabled={trabajando}>Cancelar</button>
          <button className="primario" onClick={guardar} disabled={trabajando}>
            {trabajando ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <Error error={error} />

      <div className="campo">
        <label htmlFor="nombre">Nombre *</label>
        <input
          id="nombre" value={datos.nombre} maxLength={120}
          onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="apellidos">Apellidos</label>
        <input
          id="apellidos" value={datos.apellidos} maxLength={150}
          onChange={(e) => setDatos({ ...datos, apellidos: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="regionId">Región de despacho</label>
        <select
          id="regionId" value={datos.regionId}
          onChange={(e) => setDatos({ ...datos, regionId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {regiones.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
        </select>
        <span className="ayuda">
          Al asignar una orden se ofrecen los técnicos de la región de la sucursal.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="especialidadId">Especialidad</label>
        <select
          id="especialidadId" value={datos.especialidadId}
          onChange={(e) => setDatos({ ...datos, especialidadId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="telefono">Teléfono</label>
        <input
          id="telefono" value={datos.telefono} maxLength={30}
          onChange={(e) => setDatos({ ...datos, telefono: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="correo">Correo</label>
        <input
          id="correo" type="email" value={datos.correo} maxLength={150}
          onChange={(e) => setDatos({ ...datos, correo: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="rfc">RFC</label>
        <input
          id="rfc" className="mono" value={datos.rfc} maxLength={13}
          onChange={(e) => setDatos({ ...datos, rfc: e.target.value.toUpperCase() })}
        />
      </div>

      <h2>Datos bancarios</h2>

      <div className="campo">
        <label htmlFor="bancoId">Banco</label>
        <select
          id="bancoId" value={datos.bancoId}
          onChange={(e) => setDatos({ ...datos, bancoId: e.target.value })}
        >
          <option value="">— sin asignar —</option>
          {bancos.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
        </select>
      </div>

      <div className="campo">
        <label htmlFor="titular">Titular de la cuenta</label>
        <input
          id="titular" value={datos.titularCuenta} maxLength={200}
          onChange={(e) => setDatos({ ...datos, titularCuenta: e.target.value })}
        />
      </div>

      {/* Va con lo bancario porque es una decisión de pago, no de
          despacho: no cambia a quién se asigna ni qué zona cubre.

          Karin: «todas las TOT de ordenes del tecnico Joel son pagadas
          una semana despues y eso se le manda a contabilidad a pago, pero
          como un bulto». Lo que marca esta casilla es eso: que los vales
          de sus órdenes no salgan de uno en uno. */}
      <div className="campo ancho-total">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={datos.valesEnGrupo}
            onChange={(e) => setDatos({ ...datos, valesEnGrupo: e.target.checked })}
          />
          Sus vales de taller se pagan en grupo
        </label>
        <span className="ayuda">
          Al autorizar un vale de sus órdenes, el gasto queda aprobado pero el
          pago no sale todavía: espera en <strong>TOT → Por requerir pago</strong>,
          donde los vales de un mismo taller se suman y salen en una sola
          transferencia. <strong>Al taller se le sigue pagando a él</strong>: lo
          que cambia es cuándo y de cuántos vales a la vez.
        </span>
      </div>

      <div className="campo">
        <label htmlFor="numeroCuenta">Número de cuenta</label>
        <input
          id="numeroCuenta" className="mono" value={datos.numeroCuenta} maxLength={30}
          placeholder={esAlta ? '' : 'Escribe sólo si quieres reemplazarla'}
          onChange={(e) => setDatos({ ...datos, numeroCuenta: e.target.value })}
        />
      </div>

      <div className="campo">
        <label htmlFor="clabe">CLABE</label>
        <input
          id="clabe" className="mono" value={datos.clabe} maxLength={25}
          placeholder={esAlta ? '18 dígitos' : 'Escribe sólo si quieres reemplazarla'}
          onChange={(e) => setDatos({ ...datos, clabe: e.target.value })}
        />
        <span className="ayuda">
          Se comprueba el dígito verificador antes de guardar.
        </span>
      </div>
    </Dialogo>
  );
}

function CambioEstadoTecnico({
  tecnico, onCerrar, onHecho,
}: {
  tecnico: Tecnico;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function confirmar() {
    setError(null);
    setTrabajando(true);
    try {
      const accion = tecnico.activo ? 'desactivar' : 'reactivar';
      await api.post(`/tecnicos/${tecnico.id}/${accion}`, { motivo });
      onHecho();
    } catch (e) {
      setError(e);
      setTrabajando(false);
    }
  }

  return (
    <DialogoMotivo
      titulo={`${tecnico.activo ? 'Dar de baja' : 'Reactivar'} técnico`}
      textoBoton={tecnico.activo ? 'Dar de baja' : 'Reactivar'}
      motivo={motivo}
      setMotivo={setMotivo}
      trabajando={trabajando}
      error={error}
      onConfirmar={confirmar}
      onCerrar={onCerrar}
      descripcion={
        <p>
          <strong>{tecnico.nombreCompleto}</strong>
          {tecnico.activo
            ? ' dejará de aparecer al asignar órdenes. Las que ya tiene asignadas no cambian.'
            : ' volverá a estar disponible para asignaciones.'}
        </p>
      }
    />
  );
}
